<script>
  // Detail popup for a wants-board row: the full wanter list (with each
  // wanter's specificity and a link to their want page), the holders the
  // viewer can reach, and the viewer's own copies. Mirrors SearchCardDetail's
  // overlay pattern (backdrop-click-to-close via target===currentTarget,
  // Escape-to-close + body scroll lock via onMount, a11y-ignored backdrop).
  import { onMount } from 'svelte';
  import { fetchBoardDetail } from '../../lib/services/wants-board-api.js';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { buildWantListUrl } from '../../lib/logic/wantlist-logic.js';
  import { formatPrice } from '../../lib/logic/card-collection-logic.js';

  let { item, scopeParams, currencySymbol = '$', onClose } = $props();

  let detail = $state(null);
  let failed = $state(false);

  const imageUrl = $derived(getCardImageUrl(item.imageRef, 'normal'));

  // Epoch guard against stale-response races (same idiom as WantsSection's
  // loadToken): switching cards quickly must not let an earlier card's
  // response land after a newer fetch has already started.
  let loadToken = 0;

  $effect(() => {
    const cardName = item.cardName;
    const token = ++loadToken;
    detail = null;
    failed = false;
    fetchBoardDetail({ ...scopeParams, cardName }).then((res) => {
      if (token !== loadToken) return; // a newer load superseded this one
      if (res.ok) detail = res.data;
      else failed = true;
    });
  });

  onMount(() => {
    function handleKeydown(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', handleKeydown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = '';
    };
  });

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose?.();
  }

  function specLabel(entry) {
    if (!entry.set_code && !entry.finish) return 'any printing';
    const bits = [];
    if (entry.set_code) bits.push(entry.set_code.toUpperCase());
    if (entry.collector_number) bits.push(`#${entry.collector_number}`);
    if (entry.finish) bits.push(entry.finish);
    return bits.join(' ');
  }
</script>

<!-- Backdrop: click-outside closes; keyboard close is the document Escape listener in onMount -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div class="detail-backdrop" onclick={handleBackdropClick}>
  <div class="detail-content" role="dialog" aria-label="{item.name} wants detail">
    <button class="detail-close" onclick={onClose} aria-label="Close">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>

    <div class="detail-layout">
      <div class="detail-image-area">
        {#if imageUrl}
          <img class="detail-card-image" src={imageUrl} alt={item.name} />
        {:else}
          <div class="image-placeholder"></div>
        {/if}
      </div>

      <div class="detail-info">
        <h2 class="detail-card-name">{item.name}</h2>
        {#if item.price > 0}
          <p class="detail-price">from {formatPrice(item.price, currencySymbol)}</p>
        {/if}

        {#if failed}
          <p class="detail-status">Couldn't load details.</p>
        {:else if !detail}
          <p class="detail-status">Loading…</p>
        {:else}
          <div class="detail-section">
            <h3 class="detail-section-title">Wanted by</h3>
            <ul class="detail-list">
              {#each detail.wanters as w (w.username)}
                <li>
                  <a href={buildWantListUrl(w.username)}>{w.display_name}</a>
                  <span class="spec">{w.entries.map(specLabel).join(', ')}</span>
                  {#if !w.can_reach}
                    <span
                      class="bridge-tag"
                      title="They can't reach any holder — only you can connect them">only you</span
                    >
                  {/if}
                </li>
              {/each}
            </ul>
          </div>

          {#if detail.your_binders.length > 0}
            <div class="detail-section">
              <h3 class="detail-section-title">Your copies</h3>
              <ul class="detail-list">
                {#each detail.your_binders as b (b.binder_url)}
                  <li><a href={b.binder_url}>{b.binder_name}</a> ×{b.quantity}</li>
                {/each}
              </ul>
            </div>
          {/if}

          {#if detail.holders.length > 0}
            <div class="detail-section">
              <h3 class="detail-section-title">Who has it</h3>
              <ul class="detail-list">
                {#each detail.holders as h (h.binder_url + h.owner_name)}
                  <li>
                    {h.owner_name} — <a href={h.binder_url}>{h.binder_name}</a> ×{h.quantity}
                    {#if h.price > 0}<span class="spec">{formatPrice(h.price, currencySymbol)}</span
                      >{/if}
                  </li>
                {/each}
              </ul>
            </div>
          {/if}
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  .detail-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(44, 36, 25, 0.25);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-xl);
    animation: fade-in 0.15s ease-out;
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .detail-content {
    position: relative;
    background: var(--color-bg);
    border-radius: 18px;
    border: 1px solid var(--color-border-subtle);
    max-width: 640px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 16px 48px rgba(44, 36, 25, 0.15);
    animation: detail-slide-up 0.25s ease-out;
  }

  @keyframes detail-slide-up {
    from {
      opacity: 0;
      transform: translateY(16px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .detail-close {
    position: absolute;
    top: 18px;
    right: 18px;
    z-index: 10;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-text-tertiary);
    transition: background 0.1s;
  }

  .detail-close:hover {
    background: var(--color-accent-bg);
  }

  .detail-layout {
    display: flex;
    gap: 1.25rem;
    padding: var(--space-xl);
  }

  .detail-image-area {
    flex-shrink: 0;
    width: 220px;
  }

  .detail-card-image {
    width: 100%;
    height: auto;
    border-radius: 4%;
    display: block;
    box-shadow: 0 4px 12px rgba(40, 30, 15, 0.12);
  }

  .image-placeholder {
    width: 100%;
    aspect-ratio: 5 / 7;
    border-radius: 4%;
    background: var(--color-border-subtle);
  }

  .detail-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-card-name {
    font-family: var(--font-serif);
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0;
    line-height: 1.3;
  }

  .detail-price {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: 0.8125rem;
  }

  .detail-section {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .detail-section-title {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
    font-family: var(--font-mono);
  }

  .detail-list {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 0.8125rem;
  }

  .detail-list li {
    padding: 4px 0;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  .detail-status {
    color: var(--color-text-tertiary);
    font-size: 0.8125rem;
  }

  .spec {
    font-size: 0.75rem;
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
  }

  .bridge-tag {
    font-size: 0.6875rem;
    font-weight: 600;
    font-family: var(--font-mono);
    border: 1px solid var(--color-text-muted);
    border-radius: var(--radius-sm);
    padding: 1px 5px;
  }

  @media (max-width: 520px) {
    .detail-layout {
      flex-direction: column;
      padding: 1rem;
    }

    .detail-image-area {
      width: 100%;
      max-width: 260px;
      margin: 0 auto;
    }

    .detail-content {
      max-height: 95vh;
    }

    .detail-backdrop {
      padding: var(--space-md);
    }
  }
</style>
